import { UserIdentityStatusEnum } from "@/components/types/UserIdentity.Status";
import type { IdentityRole } from "@/screen/Identities/Interfaces/IdentitiesMainScreenInterfaces";
import { hasAcceptTransferParam } from "@/screen/Identities/identitiesRoutes";
import { isEmptyMainBirthDraft, isMainNewDraft, normalizeIdentityStatus } from "@/screen/Identities/holderIdentityDraft";
import { UserIdentityFlowAction } from "@/services/UserIdentitiesService";

/** Lo mínimo del listado byUser que hace falta para decidir si se ofrece aceptar como principal. */
export interface AcceptTransferAccountIdentity
{
    id: number;
    role: IdentityRole;
    isMain?: boolean;
    status?: string | null;
}

/** Lo mínimo del detalle del principal: el número de documento llega como `dni` (alias `idDocument`). */
export interface AcceptTransferMainDetail
{
    id: number;
    main?: boolean | null;
    status?: string | null;
    dni?: string | null;
    idDocument?: string | null;
}

/**
 * Interviniente recibido por cesión aprobada y pendiente de que su nuevo propietario la acepte. El backend
 * rechaza cualquier operación sobre él (TRANSFER_PENDING_ACCEPTANCE) hasta que se acepta.
 */
export function isTransferPendingAcceptance(status: string | null | undefined): boolean
{
    return normalizeIdentityStatus(status) === UserIdentityStatusEnum.Transfer;
}

/**
 * Identidad cuya edición hay que abrir cuando el backend responde AcceptTransferOwnership (al analizar el DNI o
 * al guardar el borrador con el DNI de un interviniente propio en TRANSFER). Null si la acción es otra o no
 * llega un id válido: en ese caso no hay a dónde navegar.
 */
export function resolveAcceptTransferTargetId(action: UserIdentityFlowAction | null | undefined, existingIdentityId: number | null | undefined): number | null
{
    if(action !== UserIdentityFlowAction.AcceptTransferOwnership)
    {
        return null;
    }
    const id = Number(existingIdentityId ?? 0);
    return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Paso 1 (listado byUser): quitando el interviniente recibido, entre los propios (rol Owner) queda exactamente
 * uno y es el principal en NEW. Devuelve su id, que hay que confirmar con su detalle, o null si no aplica.
 */
export function findEmptyMainBirthCandidateId(identities: readonly AcceptTransferAccountIdentity[] | null | undefined, transferredIdentityId: number): number | null
{
    if(!identities)
    {
        return null;
    }

    const otherOwnIdentities = identities.filter((identity) => identity.role === "Owner" && identity.id !== transferredIdentityId);
    if(otherOwnIdentities.length !== 1)
    {
        return null;
    }

    const onlyOwnIdentity = otherOwnIdentities[0];
    if(onlyOwnIdentity.isMain !== true || !isMainNewDraft(onlyOwnIdentity.status) || !(onlyOwnIdentity.id > 0))
    {
        return null;
    }
    return onlyOwnIdentity.id;
}

/**
 * Decide si el formulario de aceptación ofrece «Aceptar como interviniente principal»: la cuenta solo tiene el
 * principal vacío de nacimiento (paso 1 con el listado) y su detalle confirma que es Main, NEW y sin documento
 * (paso 2). Ante cualquier duda (datos ausentes o que no cuadran) devuelve false y se ofrece solo «Aceptar la
 * cesión». El backend lo vuelve a validar al aceptar.
 */
export function canAcceptTransferAsMain(identities: readonly AcceptTransferAccountIdentity[] | null | undefined, transferredIdentityId: number, mainDetail: AcceptTransferMainDetail | null | undefined): boolean
{
    const candidateId = findEmptyMainBirthCandidateId(identities, transferredIdentityId);
    if(candidateId === null || !mainDetail || Number(mainDetail.id) !== candidateId)
    {
        return false;
    }

    const storedDocument = String(mainDetail.dni ?? "").trim() !== "" ? mainDetail.dni : mainDetail.idDocument;
    return isEmptyMainBirthDraft({main: mainDetail.main, status: mainDetail.status, dni: storedDocument});
}

/**
 * El enlace antiguo del aviso (`?acceptTransfer=1`) solo tiene sentido sobre un interviniente en TRANSFER, donde el
 * formulario de aceptación sale igual sin él. Sobre cualquier otro estado se quita de la URL.
 */
export function shouldRemoveAcceptTransferParam(search: string | null | undefined, loadedStatus: string | null | undefined): boolean
{
    return hasAcceptTransferParam(search) && !isTransferPendingAcceptance(loadedStatus);
}
